import Hls from 'hls.js';

/**
 * LRU cache for signed URLs to avoid redundant API calls for the same segment.
 * Signed URLs are cached with a TTL so they can be reused within their validity window.
 */
class SignedUrlCache {
  constructor({ maxSize = 500, ttlMs = 5 * 60 * 1000 } = {}) {
    this.maxSize = maxSize;
    this.ttlMs = ttlMs;
    this.cache = new Map();
  }

  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Check if the entry has expired
    if (Date.now() - entry.timestamp > this.ttlMs) {
      this.cache.delete(key);
      return null;
    }

    // Move to end for LRU ordering
    this.cache.delete(key);
    this.cache.set(key, entry);
    return entry.signedUrl;
  }

  set(key, signedUrl) {
    // Evict oldest entry if at capacity
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }

    this.cache.set(key, {
      signedUrl,
      timestamp: Date.now(),
    });
  }

  clear() {
    this.cache.clear();
  }
}

/**
 * Creates a custom hls.js loader class that intercepts segment (fragment) requests,
 * calls the backend API to get a signed S3 CDN URL, then loads the segment from
 * that signed URL instead of the original.
 *
 * Playlist/manifest requests are passed through with the existing RawLoader behavior
 * (parsing manifest data to update recording end dates for live sessions).
 *
 * @param {Object} options
 * @param {Function} options.getApiClient - Returns the ApiClient instance
 * @param {Function} options.getSessionParams - Returns { installSid, examSid, sessionSid }
 * @param {Function} [options.onManifestResponse] - Callback for manifest responses (for RawLoader behavior)
 * @param {Object} [options.cacheOptions] - Options for the signed URL cache { maxSize, ttlMs }
 * @returns {Class} - A custom hls.js loader class
 */
export function createSignedUrlLoader({
  getApiClient,
  getSessionParams,
  onManifestResponse,
  cacheOptions = {},
}) {
  const signedUrlCache = new SignedUrlCache(cacheOptions);

  // In-flight request deduplication: prevents multiple concurrent API calls for the same segment
  const pendingRequests = new Map();

  /**
   * Fetches a signed URL for a given segment URL.
   * Uses cache and request deduplication to minimize API calls.
   *
   * @param {string} segmentUrl - The original segment URL
   * @returns {Promise<string>} - The signed CDN URL
   */
  async function getSignedUrl(segmentUrl) {
    // Check cache first
    const cached = signedUrlCache.get(segmentUrl);
    if (cached) {
      return cached;
    }

    // Deduplicate in-flight requests for the same segment
    if (pendingRequests.has(segmentUrl)) {
      return pendingRequests.get(segmentUrl);
    }

    const requestPromise = (async () => {
      try {
        const apiClient = getApiClient();
        const sessionParams = getSessionParams();

        if (!apiClient || !sessionParams) {
          console.warn('[SignedUrlLoader] ApiClient or session params not available, using original URL');
          return segmentUrl;
        }

        const result = await apiClient.getSignedSegmentUrl({
          installSid: sessionParams.installSid,
          examSid: sessionParams.examSid,
          sessionSid: sessionParams.sessionSid,
          segmentUrl,
          ttl: Math.floor(signedUrlCache.ttlMs / 1000),
        });

        if (result.successful && result.data?.signedUrl) {
          signedUrlCache.set(segmentUrl, result.data.signedUrl);
          return result.data.signedUrl;
        }

        console.warn('[SignedUrlLoader] Failed to get signed URL, falling back to original:', segmentUrl);
        return segmentUrl;
      } catch (error) {
        console.error('[SignedUrlLoader] Error getting signed URL:', error);
        return segmentUrl;
      } finally {
        pendingRequests.delete(segmentUrl);
      }
    })();

    pendingRequests.set(segmentUrl, requestPromise);
    return requestPromise;
  }

  /**
   * Determines if a URL is a segment/fragment request (as opposed to a playlist/manifest).
   * Segments typically end in .ts, .m4s, .mp4, .aac, etc.
   *
   * @param {string} url - The URL to check
   * @returns {boolean}
   */
  function isSegmentRequest(url) {
    if (!url) return false;
    const path = url.split('?')[0].toLowerCase();
    return (
      path.endsWith('.ts') ||
      path.endsWith('.m4s') ||
      path.endsWith('.mp4') ||
      path.endsWith('.aac') ||
      path.endsWith('.fmp4')
    );
  }

  class SignedUrlHlsLoader extends Hls.DefaultConfig.loader {
    load(context, config, callbacks) {
      const originalUrl = context.url;

      // For segment/fragment requests, intercept and swap to signed URL
      if (isSegmentRequest(originalUrl)) {
        getSignedUrl(originalUrl)
          .then((signedUrl) => {
            // Replace the URL in the context with the signed CDN URL
            context.url = signedUrl;
            super.load(context, config, callbacks);
          })
          .catch((error) => {
            console.error('[SignedUrlLoader] Failed to resolve signed URL, loading original:', error);
            super.load(context, config, callbacks);
          });
        return;
      }

      // For playlist/manifest requests, pass through with manifest parsing callback
      super.load(context, config, {
        onSuccess: (response, stats, ctx, xhr) => {
          // Call the manifest response handler if provided (preserves RawLoader behavior)
          if (onManifestResponse && typeof response.data === 'string') {
            onManifestResponse(response.data);
          }
          callbacks.onSuccess(response, stats, ctx, xhr);
        },
        onError: callbacks.onError,
        onTimeout: callbacks.onTimeout,
        onProgress: callbacks.onProgress,
      });
    }
  }

  // Expose cache clearing for cleanup
  SignedUrlHlsLoader.clearCache = () => signedUrlCache.clear();

  // Expose getSignedUrl so external systems (e.g. prefetch) can resolve signed URLs
  SignedUrlHlsLoader.getSignedUrl = getSignedUrl;

  return SignedUrlHlsLoader;
}
